﻿import {isNullOrEmpty, NamedValue, translation} from "@2d-front-monorepo/core";
import {
	initializeMvvmContext,
	MvvmDataProvider,
	MvvmDataProviderRef,
	MvvmSelect
} from "@2d-front-monorepo/design-library";
import {RequiredResidencyProperties} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, RefObject, useMemo} from "react";
import {Attachments} from "../Attachments";
import AttachmentType from "../enums/AttachmentType";
import {residencyToCompactString} from "../helpers/residencyToCompactString";
import {ResidencyId} from "../nominalTypes/ResidencyId";
import {IntegraHolderData} from "../types/IntegraHolderData";
import {IntegraZone} from "../types/IntegraZone";
import {ResidencyResult} from "../types/ResidencyResult";
import {ComponentParts, ComponentWithChildrenParts} from "./ComponentParts";
import CreateAttachmentsComponent from "./CreateAttachmentsComponent";
import CreateResidencyComponent, {CreateResidencyModel} from "./CreateResidencyComponent";
import PermitsMvvmErrorCollector from "./PermitsMvvmErrorCollector";
import ResidencyComponent from "./ResidencyComponent";

export interface CreateOrSelectResidencyComponentProps extends ComponentParts {
	autoFocus?: boolean;
	contextRef?: RefObject<MvvmDataProviderRef<CreateOrSelectResidencyModel>>;
	createEnabled?: boolean;
	createParts?: ComponentWithChildrenParts;
	filesPrefix: string;
	hiddenProperties?: RequiredResidencyProperties;
	holder: IntegraHolderData;
	initialState: CreateOrSelectResidencyModel;
	onSubmit: (formData: CreateOrSelectResidencyModel) => Promise<void>;
	readOnlyProperties?: RequiredResidencyProperties;
	requiredAttachments: AttachmentType[];
	requiredProperties: RequiredResidencyProperties;
	residencies?: ResidencyResult[];
	selectParts?: ComponentWithChildrenParts;
	zones: IntegraZone[];
}

export type CreateOrSelectResidencyModel = {
	residencyId: ResidencyId;
} & CreateResidencyModel;

function createSelectItems(residencies: ResidencyResult[] | undefined, createEnabled: boolean | undefined): NamedValue<ResidencyId>[] {
	const result: NamedValue<ResidencyId>[] = [];
	if (residencies && residencies.length > 0) {
		result.push(...residencies.map(r => {
			return {value: r.id, label: residencyToCompactString(r)}
		}));
	}

	if (createEnabled) {
		result.push({
			label: translation(PermitTranslations.add_new),
			value: "create"
		});
	}

	return result;
}

export default function CreateOrSelectResidencyComponent(props: CreateOrSelectResidencyComponentProps): JSX.Element {
	const {
		initialState,
		onSubmit,
		zones,
		holder,
		filesPrefix,
		residencies,
		contextRef,
		createEnabled,
		header,
		footer,
		requiredProperties,
		readOnlyProperties,
		hiddenProperties,
		requiredAttachments,
		autoFocus,
		createParts,
		selectParts
	} = props;

	const choose = useMemo(() => createSelectItems(residencies, createEnabled), [residencies, createEnabled]);
	const multiple = choose.length > 1;

	const context = initializeMvvmContext({
		initialState: () => {
			initialState.residencyId = isNullOrEmpty(initialState.residencyId) ? choose.length > 0 ? choose[0].value : "create" : initialState.residencyId;
			return initialState;
		},
		contextRef: contextRef
	});

	const state = context.state;
	const selectedResidency = useMemo(() => residencies?.find(r => r.id === state.residencyId), [state.residencyId]);
	const expiredAttachments = useMemo(() => Attachments.findExpiredTypes(selectedResidency, requiredAttachments), [selectedResidency, requiredAttachments]);
	const replaceableAttachments = useMemo(() => Attachments.findSubmitted(selectedResidency), [selectedResidency]);
	const createVisible = createEnabled && state.residencyId === "create";

	return <MvvmDataProvider<CreateOrSelectResidencyModel> onSubmit={onSubmit} context={context}>
		<PermitsMvvmErrorCollector/>
		{header}
		{multiple && <MvvmSelect context={context}
                                 label={translation(PermitTranslations.new_residency__choose)}
                                 property="residencyId"
                                 items={choose}/>}

		{selectedResidency && <div className="layout vertical gap">
			{selectParts?.header}
            <ResidencyComponent residency={selectedResidency}
                                holder={holder}
                                filesPrefix={filesPrefix}/>
			{selectParts?.children}
            <CreateAttachmentsComponent context={context}
                                        label={translation(PermitTranslations.documentation__required)}
                                        types={expiredAttachments}
                                        required={true}/>

            <CreateAttachmentsComponent context={context}
                                        label={translation(PermitTranslations.documentation__update)}
                                        types={replaceableAttachments}
                                        required={false}/>
			{selectParts?.footer}
        </div>}

		{createVisible &&
            <CreateResidencyComponent context={context}
                                      requiredAttachments={requiredAttachments}
                                      requiredProperties={requiredProperties}
                                      readOnlyProperties={readOnlyProperties}
                                      hiddenProperties={hiddenProperties}
                                      zones={zones}
                                      autoFocus={autoFocus}
                                      header={createParts?.header}
                                      children={createParts?.children}
                                      footer={createParts?.footer}/>
		}
		{footer}
	</MvvmDataProvider>;
}
